import { useContext, useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import { useTextDomainContext } from "@/app/providers";

/**
 * Custom hook to monitor network connectivity status and display notifications.
 * Listens to browser online/offline events and shows appropriate snackbar messages.
 * Automatically closes the offline notification when connection is restored.
 *
 * @returns {void}
 */
export const useNetworkStatus = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const offlineSnackbarRef = useRef(null);

  useEffect(() => {
    const handleOnline = () => {
      // Close the offline notification if it exists
      if (offlineSnackbarRef.current) {
        closeSnackbar(offlineSnackbarRef.current);
        offlineSnackbarRef.current = null;
      }

      // Show success message
      enqueueSnackbar(gettext("Internet connection restored"), {
        variant: "success",
      });
    };

    const handleOffline = () => {
      // Store the snackbar key so we can close it later
      const key = enqueueSnackbar(
        gettext("No internet connection. Please check your network."),
        {
          variant: "error",
          persist: true, // Keep the notification visible until dismissed or connection restored
        },
      );
      offlineSnackbarRef.current = key;
    };

    // Add event listeners
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);

      // Close any pending offline notification on unmount
      if (offlineSnackbarRef.current) {
        closeSnackbar(offlineSnackbarRef.current);
      }
    };
  }, [enqueueSnackbar, closeSnackbar, gettext]);
};
